import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './HelpModal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('intro');

  if (!isOpen) return null;

  const sections = [
    { id: 'intro', title: t('help.sections.intro'), icon: '🌍' },
    { id: 'getting-started', title: t('help.sections.gettingStarted'), icon: '🚀' },
    { id: 'basic-features', title: t('help.sections.basicFeatures'), icon: '🗺️' },
    { id: 'user-layers', title: t('help.sections.userLayers'), icon: '📁' },
    { id: 'advanced-features', title: t('help.sections.advancedFeatures'), icon: '🎨' },
    { id: 'troubleshooting', title: t('help.sections.troubleshooting'), icon: '🔧' },
    { id: 'faq', title: t('help.sections.faq'), icon: '❓' }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'intro':
        return (
          <div className="help-section">
            <h2>{t('help.intro.title')}</h2>
            <p>{t('help.intro.description')}</p>
            
            <h3>{t('help.intro.features.title')}</h3>
            <ul>
              <li><strong>{t('help.intro.features.egis')}</strong> - {t('help.intro.features.egisDesc')}</li>
              <li><strong>{t('help.intro.features.userLayers')}</strong> - {t('help.intro.features.userLayersDesc')}</li>
              <li><strong>{t('help.intro.features.multilingual')}</strong> - {t('help.intro.features.multilingualDesc')}</li>
              <li><strong>{t('help.intro.features.realtime')}</strong> - {t('help.intro.features.realtimeDesc')}</li>
              <li><strong>{t('help.intro.features.rotation')}</strong> - {t('help.intro.features.rotationDesc')}</li>
            </ul>

            <h3>{t('help.intro.fileFormats.title')}</h3>
            <ul>
              <li><strong>TIF/TIFF</strong> - {t('help.intro.fileFormats.tif')}</li>
              <li><strong>{t('help.intro.fileFormats.classification')}</strong> - {t('help.intro.fileFormats.classificationDesc')}</li>
              <li><strong>RGB {t('help.intro.fileFormats.images')}</strong> - {t('help.intro.fileFormats.rgbDesc')}</li>
            </ul>
          </div>
        );

      case 'getting-started':
        return (
          <div className="help-section">
            <h2>{t('help.gettingStarted.title')}</h2>
            
            <h3>1. {t('help.gettingStarted.access.title')}</h3>
            <p>{t('help.gettingStarted.access.description')}</p>
            <ul>
              <li>Docker 배포 시: <code>http://localhost:3000</code></li>
              <li>로컬 개발 시: <code>http://localhost:3000</code></li>
            </ul>

            <h3>2. {t('help.gettingStarted.interface.title')}</h3>
            <div className="interface-diagram">
              <pre>{`
┌─────────────────────────────────────────────────────────┐
│                    EcoVision                            │
├─────────────────────────────────────────────────────────┤
│ 왼쪽 패널        │           메인 지도 영역              │
│ - 레이어 목록    │                                        │
│ - 파일 관리      │                                        │
│ - 업로드        │                                        │
├─────────────────────────────────────────────────────────┤
│ 오른쪽 패널                                              │
│ - 배경지도 선택                                          │
│ - 범례 표시                                              │
│ - 투명도 조절                                            │
└─────────────────────────────────────────────────────────┘
              `}</pre>
            </div>

            <h3>3. {t('help.gettingStarted.language.title')}</h3>
            <p>{t('help.gettingStarted.language.description')}</p>
            <ul>
              <li>{t('help.gettingStarted.language.supported')}: 한국어, 영어, 프랑스어</li>
              <li>{t('help.gettingStarted.language.autoSave')}</li>
            </ul>
          </div>
        );

      case 'basic-features':
        return (
          <div className="help-section">
            <h2>{t('help.basicFeatures.title')}</h2>
            
            <h3>{t('help.basicFeatures.backgroundMap.title')}</h3>
            <ol>
              <li><strong>{t('help.basicFeatures.backgroundMap.step1')}</strong></li>
              <li><strong>{t('help.basicFeatures.backgroundMap.step2')}</strong>:
                <ul>
                  <li><strong>OpenStreetMap</strong> - {t('help.basicFeatures.backgroundMap.options.osm')}</li>
                  <li><strong>CartoDB Positron</strong> - {t('help.basicFeatures.backgroundMap.options.cartodb')}</li>
                  <li><strong>{t('help.basicFeatures.backgroundMap.options.satellite')}</strong> - {t('help.basicFeatures.backgroundMap.options.satelliteDesc')}</li>
                  <li><strong>{t('help.basicFeatures.backgroundMap.options.topographic')}</strong> - {t('help.basicFeatures.backgroundMap.options.topographicDesc')}</li>
                </ul>
              </li>
            </ol>

            <h3>{t('help.basicFeatures.mapControls.title')}</h3>
            <ul>
              <li><strong>{t('help.basicFeatures.mapControls.zoom')}</strong> - {t('help.basicFeatures.mapControls.zoomDesc')}</li>
              <li><strong>{t('help.basicFeatures.mapControls.pan')}</strong> - {t('help.basicFeatures.mapControls.panDesc')}</li>
              <li><strong>{t('help.basicFeatures.mapControls.center')}</strong> - {t('help.basicFeatures.mapControls.centerDesc')}</li>
            </ul>

            <h3>{t('help.basicFeatures.egis.title')}</h3>
            <ol>
              <li><strong>{t('help.basicFeatures.egis.step1')}</strong></li>
              <li><strong>{t('help.basicFeatures.egis.step2')}</strong></li>
              <li><strong>{t('help.basicFeatures.egis.step3')}</strong> (0~100%)</li>
              <li><strong>{t('help.basicFeatures.egis.step4')}</strong></li>
            </ol>
          </div>
        );

      case 'user-layers':
        return (
          <div className="help-section">
            <h2>{t('help.userLayers.title')}</h2>
            
            <h3>{t('help.userLayers.upload.title')}</h3>
            <ol>
              <li><strong>{t('help.userLayers.upload.step1')}</strong></li>
              <li><strong>{t('help.userLayers.upload.step2')}</strong> (예: "드론 사진")</li>
              <li><strong>{t('help.userLayers.upload.step3')}</strong></li>
              <li><strong>{t('help.userLayers.upload.step4')}</strong></li>
              <li><strong>{t('help.userLayers.upload.step5')}</strong></li>
            </ol>

            <h3>{t('help.userLayers.management.title')}</h3>
            <ul>
              <li><strong>{t('help.userLayers.management.toggle')}</strong> - {t('help.userLayers.management.toggleDesc')}</li>
              <li><strong>{t('help.userLayers.management.fileControl')}</strong> - {t('help.userLayers.management.fileControlDesc')}</li>
              <li><strong>{t('help.userLayers.management.order')}</strong> - {t('help.userLayers.management.orderDesc')}</li>
            </ul>

            <h3>{t('help.userLayers.settings.title')}</h3>
            <ol>
              <li><strong>{t('help.userLayers.settings.step1')}</strong></li>
              <li><strong>{t('help.userLayers.settings.step2')}</strong></li>
            </ol>

            <h4>{t('help.userLayers.settings.rotation.title')}</h4>
            <ul>
              <li><strong>{t('help.userLayers.settings.rotation.manual')}</strong> - 90°, 180°, 270° {t('help.userLayers.settings.rotation.manualDesc')}</li>
              <li><strong>{t('help.userLayers.settings.rotation.exif')}</strong> - {t('help.userLayers.settings.rotation.exifDesc')}</li>
              <li><strong>{t('help.userLayers.settings.rotation.realtime')}</strong> - {t('help.userLayers.settings.rotation.realtimeDesc')}</li>
            </ul>

            <h4>{t('help.userLayers.settings.coordinate.title')}</h4>
            <ul>
              <li><strong>{t('help.userLayers.settings.coordinate.default')}</strong> - {t('help.userLayers.settings.coordinate.defaultDesc')}</li>
              <li><strong>{t('help.userLayers.settings.coordinate.override')}</strong> - {t('help.userLayers.settings.coordinate.overrideDesc')}</li>
              <li><strong>{t('help.userLayers.settings.coordinate.selection')}</strong> - {t('help.userLayers.settings.coordinate.selectionDesc')}</li>
            </ul>
          </div>
        );

      case 'advanced-features':
        return (
          <div className="help-section">
            <h2>{t('help.advancedFeatures.title')}</h2>
            
            <h3>{t('help.advancedFeatures.legend.title')}</h3>
            <ol>
              <li><strong>{t('help.advancedFeatures.legend.step1')}</strong></li>
              <li><strong>{t('help.advancedFeatures.legend.step2')}</strong></li>
              <li><strong>{t('help.advancedFeatures.legend.step3')}</strong></li>
            </ol>

            <h3>{t('help.advancedFeatures.legendEditor.title')}</h3>
            <ol>
              <li><strong>{t('help.advancedFeatures.legendEditor.step1')}</strong></li>
              <li><strong>{t('help.advancedFeatures.legendEditor.step2')}</strong>:
                <ul>
                  <li><strong>{t('help.advancedFeatures.legendEditor.options.name')}</strong> - {t('help.advancedFeatures.legendEditor.options.nameDesc')}</li>
                  <li><strong>{t('help.advancedFeatures.legendEditor.options.color')}</strong> - {t('help.advancedFeatures.legendEditor.options.colorDesc')}</li>
                  <li><strong>{t('help.advancedFeatures.legendEditor.options.opacity')}</strong> - {t('help.advancedFeatures.legendEditor.options.opacityDesc')}</li>
                </ul>
              </li>
              <li><strong>{t('help.advancedFeatures.legendEditor.step3')}</strong></li>
            </ol>

            <h3>{t('help.advancedFeatures.opacity.title')}</h3>
            <ul>
              <li><strong>{t('help.advancedFeatures.opacity.layer')}</strong> - {t('help.advancedFeatures.opacity.layerDesc')}</li>
              <li><strong>{t('help.advancedFeatures.opacity.range')}</strong> - 0% ({t('help.advancedFeatures.opacity.transparent')}) ~ 100% ({t('help.advancedFeatures.opacity.opaque')})</li>
              <li><strong>{t('help.advancedFeatures.opacity.realtime')}</strong> - {t('help.advancedFeatures.opacity.realtimeDesc')}</li>
            </ul>

            <h3>{t('help.advancedFeatures.navigation.title')}</h3>
            <ol>
              <li><strong>{t('help.advancedFeatures.navigation.step1')}</strong></li>
              <li><strong>{t('help.advancedFeatures.navigation.step2')}</strong></li>
              <li><strong>{t('help.advancedFeatures.navigation.step3')}</strong></li>
            </ol>
          </div>
        );

      case 'troubleshooting':
        return (
          <div className="help-section">
            <h2>{t('help.troubleshooting.title')}</h2>
            
            <h3>{t('help.troubleshooting.commonIssues.title')}</h3>
            
            <h4>1. {t('help.troubleshooting.commonIssues.tifNotDisplayed.title')}</h4>
            <p><strong>{t('help.troubleshooting.commonIssues.tifNotDisplayed.cause')}</strong>: {t('help.troubleshooting.commonIssues.tifNotDisplayed.causeDesc')}</p>
            <p><strong>{t('help.troubleshooting.commonIssues.tifNotDisplayed.solution')}</strong>:</p>
            <ol>
              <li>{t('help.troubleshooting.commonIssues.tifNotDisplayed.solution1')}</li>
              <li>{t('help.troubleshooting.commonIssues.tifNotDisplayed.solution2')}</li>
              <li>{t('help.troubleshooting.commonIssues.tifNotDisplayed.solution3')}</li>
            </ol>

            <h4>2. {t('help.troubleshooting.commonIssues.rotatedImage.title')}</h4>
            <p><strong>{t('help.troubleshooting.commonIssues.rotatedImage.cause')}</strong>: {t('help.troubleshooting.commonIssues.rotatedImage.causeDesc')}</p>
            <p><strong>{t('help.troubleshooting.commonIssues.rotatedImage.solution')}</strong>:</p>
            <ol>
              <li>{t('help.troubleshooting.commonIssues.rotatedImage.solution1')}</li>
              <li>{t('help.troubleshooting.commonIssues.rotatedImage.solution2')}</li>
              <li>{t('help.troubleshooting.commonIssues.rotatedImage.solution3')}</li>
            </ol>

            <h4>3. {t('help.troubleshooting.commonIssues.legendNotApplied.title')}</h4>
            <p><strong>{t('help.troubleshooting.commonIssues.legendNotApplied.cause')}</strong>: {t('help.troubleshooting.commonIssues.legendNotApplied.causeDesc')}</p>
            <p><strong>{t('help.troubleshooting.commonIssues.legendNotApplied.solution')}</strong>:</p>
            <ol>
              <li>{t('help.troubleshooting.commonIssues.legendNotApplied.solution1')}</li>
              <li>{t('help.troubleshooting.commonIssues.legendNotApplied.solution2')}</li>
              <li>{t('help.troubleshooting.commonIssues.legendNotApplied.solution3')}</li>
            </ol>

            <h4>4. {t('help.troubleshooting.commonIssues.slowPerformance.title')}</h4>
            <p><strong>{t('help.troubleshooting.commonIssues.slowPerformance.cause')}</strong>: {t('help.troubleshooting.commonIssues.slowPerformance.causeDesc')}</p>
            <p><strong>{t('help.troubleshooting.commonIssues.slowPerformance.solution')}</strong>:</p>
            <ol>
              <li>{t('help.troubleshooting.commonIssues.slowPerformance.solution1')}</li>
              <li>{t('help.troubleshooting.commonIssues.slowPerformance.solution2')}</li>
              <li>{t('help.troubleshooting.commonIssues.slowPerformance.solution3')}</li>
            </ol>

            <h3>{t('help.troubleshooting.errorMessages.title')}</h3>
            
            <h4>"{t('help.troubleshooting.errorMessages.noCoordinateSystem')}"</h4>
            <ul>
              <li>{t('help.troubleshooting.errorMessages.noCoordinateSystem.solution1')}</li>
              <li>{t('help.troubleshooting.errorMessages.noCoordinateSystem.solution2')}</li>
            </ul>

            <h4>"{t('help.troubleshooting.errorMessages.cannotReadFile')}"</h4>
            <ul>
              <li>{t('help.troubleshooting.errorMessages.cannotReadFile.solution1')}</li>
              <li>{t('help.troubleshooting.errorMessages.cannotReadFile.solution2')}</li>
            </ul>

            <h4>"{t('help.troubleshooting.errorMessages.outOfMemory')}"</h4>
            <ul>
              <li>{t('help.troubleshooting.errorMessages.outOfMemory.solution1')}</li>
              <li>{t('help.troubleshooting.errorMessages.outOfMemory.solution2')}</li>
              <li>{t('help.troubleshooting.errorMessages.outOfMemory.solution3')}</li>
            </ul>
          </div>
        );

      case 'faq':
        return (
          <div className="help-section">
            <h2>{t('help.faq.title')}</h2>
            
            <div className="faq-item">
              <h3>Q1. {t('help.faq.q1')}</h3>
              <p><strong>A:</strong> {t('help.faq.a1')}</p>
              <ul>
                <li>{t('help.faq.a1.rgb')}</li>
                <li>{t('help.faq.a1.classification')}</li>
                <li>{t('help.faq.a1.dem')}</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Q2. {t('help.faq.q2')}</h3>
              <p><strong>A:</strong> {t('help.faq.a2')}</p>
              <ul>
                <li>{t('help.faq.a2.singleFile')}</li>
                <li>{t('help.faq.a2.totalFiles')}</li>
                <li>{t('help.faq.a2.resolution')}</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Q3. {t('help.faq.q3')}</h3>
              <p><strong>A:</strong> {t('help.faq.a3')}</p>
              <ul>
                <li>{t('help.faq.a3.localStorage')}</li>
                <li>{t('help.faq.a3.containerRestart')}</li>
                <li>{t('help.faq.a3.backup')}</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Q4. {t('help.faq.q4')}</h3>
              <p><strong>A:</strong> {t('help.faq.a4')}</p>
              <ul>
                <li>{t('help.faq.a4.concurrentAccess')}</li>
                <li>{t('help.faq.a4.sequentialUpload')}</li>
                <li>{t('help.faq.a4.futureSupport')}</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Q5. {t('help.faq.q5')}</h3>
              <p><strong>A:</strong> {t('help.faq.a5')}</p>
              <ul>
                <li>{t('help.faq.a5.screenshot')}</li>
                <li>{t('help.faq.a5.legendData')}</li>
                <li>{t('help.faq.a5.futureExport')}</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Q6. {t('help.faq.q6')}</h3>
              <p><strong>A:</strong> {t('help.faq.a6')}</p>
              <ul>
                <li>{t('help.faq.a6.touchInterface')}</li>
                <li>{t('help.faq.a6.mobileUI')}</li>
                <li>{t('help.faq.a6.autoCollapse')}</li>
              </ul>
            </div>
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  };

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h1>{t('help.title')}</h1>
          <button className="help-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="help-modal-content">
          <div className="help-sidebar">
            <nav className="help-navigation">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`help-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="help-nav-icon">{section.icon}</span>
                  <span className="help-nav-text">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
          
          <div className="help-main-content">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal; 